/**
 * Helper module for robust timezone offset parsing and equivalence testing.
 */

/**
 * Parses an offset string such as "UTC+8", "GMT+08:00", "+08:00", "-0500", "UTC" into total minutes from UTC.
 */
export function parseOffsetString(str: string): number | null {
  if (!str) return null;
  const s = str.trim().toUpperCase();
  if (s === "UTC" || s === "GMT" || s === "Z") return 0;

  const match = s.match(/^(?:GMT|UTC)?\s*([+-])\s*(\d{1,2})(?::?(\d{2}))?$/i);
  if (match) {
    const sign = match[1] === "-" ? -1 : 1;
    const hours = parseInt(match[2], 10);
    const minutes = match[3] ? parseInt(match[3], 10) : 0;
    return sign * (hours * 60 + minutes);
  }
  return null;
}

/**
 * Calculates the current UTC offset in minutes for a given timezone identifier or offset string.
 */
export function getTimezoneOffsetMinutes(tz?: string, date: Date = new Date()): number | null {
  if (!tz || tz === "Unknown") return null;

  const direct = parseOffsetString(tz);
  if (direct !== null) return direct;

  // Primary method: Intl.DateTimeFormat longOffset
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "longOffset"
    });
    const parts = formatter.formatToParts(date);
    const tzPart = parts.find((p) => p.type === "timeZoneName")?.value;
    if (tzPart) {
      const parsed = parseOffsetString(tzPart);
      if (parsed !== null) return parsed;
    }
  } catch {
    // Fall through to Date diff calculation
  }

  // Fallback method: Date diff calculation
  try {
    const utcParts = new Intl.DateTimeFormat("en-US", {
      timeZone: "UTC",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    }).formatToParts(date);

    const tzParts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    }).formatToParts(date);

    const getVal = (parts: Intl.DateTimeFormatPart[], type: string) =>
      parseInt(parts.find((p) => p.type === type)?.value || "0", 10);

    const utcDate = Date.UTC(
      getVal(utcParts, "year"),
      getVal(utcParts, "month") - 1,
      getVal(utcParts, "day"),
      getVal(utcParts, "hour") % 24,
      getVal(utcParts, "minute"),
      getVal(utcParts, "second")
    );

    const tzDate = Date.UTC(
      getVal(tzParts, "year"),
      getVal(tzParts, "month") - 1,
      getVal(tzParts, "day"),
      getVal(tzParts, "hour") % 24,
      getVal(tzParts, "minute"),
      getVal(tzParts, "second")
    );

    return Math.round((tzDate - utcDate) / (60 * 1000));
  } catch {
    return null;
  }
}

/**
 * Evaluates whether two timezone strings represent the same UTC offset.
 */
export function areTimezonesEquivalent(tz1?: string, tz2?: string): boolean {
  if (!tz1 || !tz2 || tz1 === "Unknown" || tz2 === "Unknown") return false;
  if (tz1 === tz2) return true;

  const offset1 = getTimezoneOffsetMinutes(tz1);
  const offset2 = getTimezoneOffsetMinutes(tz2);

  if (offset1 !== null && offset2 !== null) {
    return offset1 === offset2;
  }

  return false;
}
